// @flow
import React, {useMemo, useState} from 'react';
import type {Node} from 'react';
import moment from 'moment';
import {useNavigate} from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  DataGrid,
  Paper,
} from '@wellstone-solutions/web';
import {Events} from '@wellstone-solutions/common';
import type {UISessionDocumentType} from '../../model/types';
import {
  useOptimisticRows,
  usePaginationState,
  getSortModel,
  RowCount,
  DEFAULT_PAGE,
} from 'modules/datagrid';
import {useStores} from 'hooks/useStores';
import {
  useSessionDocumentColumnMap,
  useSessionDocumentActions,
} from '../../hooks';
import {useSessionDocuments} from '../../queries';
import {DOCUMENT_STATUSES} from '../../constants';
import {ConfirmSubmit} from '../SessionDocument/View/ConfirmSubmit';
import {ConfirmDelete} from '../SessionDocument/ConfirmDelete';
import {ConfirmRevert} from '../SessionDocument/ConfirmRevert';
import {ListFilters} from './ListFilters';
import {getDocumentUrl} from '../../utils';

type PropsType = {
  columnNames: Array<string>,
  defaultParams?: {[string]: mixed},
  description?: string,
  showProgramFilter?: boolean,
  showMemberFilter?: boolean,
  showStaffFilter?: boolean,
  showGridHeader?: boolean,
  rowHeight?: () => number,
  showDocumentCount?: boolean,
  title?: string,
};

const DEFAULT_SORT_MODEL = [{field: 'dateOfService', sort: 'desc'}];

export const SessionDocumentListV2 = ({
  columnNames,
  defaultParams = {},
  description = '',
  showProgramFilter = false,
  showMemberFilter = false,
  showStaffFilter = false,
  showGridHeader = true,
  rowHeight,
  showDocumentCount = false,
  title = '',
}: PropsType): Node => {
  const navigate = useNavigate();
  const {eventStore, meStore} = useStores();
  const {removeSessionDoc, updateSessionDoc, updateSessionDocStatus} =
    useSessionDocumentActions();
  const [params, setParams] = useState(defaultParams);
  const [documentToDelete, setDocumentToDelete] = useState();
  const [documentToSubmit, setDocumentToSubmit] = useState();
  const [documentToRevert, setDocumentToRevert] = useState();

  const {
    page,
    setPage,
    pageSize,
    onPageSizeChange,
    ordering,
    onSortModelChange,
  } = usePaginationState({defaultSortModel: DEFAULT_SORT_MODEL});

  const {
    optimisticRows,
    addOptimisticRow,
    clearOptimisticRows,
    setOptimisticRows,
  } = useOptimisticRows();

  const query = useSessionDocuments({...params, page, pageSize, ordering});

  // Rows arrive already resolved to the UI shape (member/staff/typeOfService
  // joined in the query). Apply optimistic updates so the user sees instant
  // changes; `newData: false` removes the row (delete).
  const rows = useMemo(() => {
    const applyOptimisticRow = (row: UISessionDocumentType) => {
      const match = optimisticRows.find(
        ({id, idField = 'id'}) => id === row[idField],
      );
      return match ? match.newData : row;
    };

    return (query.data?.results ?? []).map(applyOptimisticRow).filter(Boolean);
  }, [query.data, optimisticRows]);

  // useSessionDocumentActions invalidates the document queries on every write,
  // which auto-refetches this list — so we only need to drop the optimistic
  // overlay here (an explicit refetch would just duplicate the request).
  const refresh = () => {
    clearOptimisticRows();
  };

  const onConfirmDelete = (documentation: UISessionDocumentType) => {
    setDocumentToDelete(documentation);
  };

  const onDelete = async () => {
    if (!documentToDelete) {
      return;
    }

    const rollbackRows = [...optimisticRows];

    addOptimisticRow({id: documentToDelete.id, newData: false});

    const response = await removeSessionDoc(documentToDelete.id);

    if (response.isSuccess) {
      refresh();
    } else {
      // rollback the optimistic update
      setOptimisticRows(rollbackRows);
    }
  };

  const onRevert = async () => {
    if (!documentToRevert) {
      return;
    }

    const rollbackRows = [...optimisticRows];

    addOptimisticRow({
      id: documentToRevert.id,
      newData: {
        ...documentToRevert,
        status: DOCUMENT_STATUSES.draft,
        reverted: {
          changedBy: meStore.me.id,
          changedAt: moment().toISOString(),
          reason: '',
        },
      },
    });

    const response = await updateSessionDocStatus(
      documentToRevert?.id,
      documentToRevert,
      DOCUMENT_STATUSES.draft,
    );

    if (response.isSuccess) {
      eventStore.addEvent(Events.REVERTED_SESSION_DOC, {
        document_id: response.data.id,
        owner_id: response.data.staff.id,
        admin_id: meStore.me.id,
      });
      refresh();
    } else {
      // rollback the optimistic update
      setOptimisticRows(rollbackRows);
    }
  };

  const onConfirmSubmitted = (documentation: UISessionDocumentType): void => {
    setDocumentToSubmit(documentation);
  };

  const onConfirmRevert = (documentation: UISessionDocumentType): void => {
    setDocumentToRevert(documentation);
  };

  const onSubmitted = async (submittedAt: string): Promise<void> => {
    if (!documentToSubmit) {
      return;
    }
    const rollbackRows = [...optimisticRows];

    addOptimisticRow({
      id: documentToSubmit.id,
      newData: {
        ...documentToSubmit,
        status: DOCUMENT_STATUSES.submitted,
        submittedAt: submittedAt,
        modified: moment(),
      },
    });

    const response = await updateSessionDoc(documentToSubmit.id, {
      ...documentToSubmit,
      submittedAt,
      status: DOCUMENT_STATUSES.submitted,
    });
    if (response.isSuccess) {
      refresh();
    } else {
      // rollback the optimistic update
      setOptimisticRows(rollbackRows);
    }
  };

  const onFilterFormSubmit = (newParams): void => {
    // Update params, but do not override any of the defaults
    setParams({
      ...newParams,
      ...defaultParams,
    });
    // Applying a new filter should return the user to the first page
    setPage(DEFAULT_PAGE);
  };

  const closeDeleteModal = (): void => {
    setDocumentToDelete();
  };

  const closeSubmittedModal = (): void => {
    setDocumentToSubmit();
  };

  const closeRevertModal = (): void => {
    setDocumentToRevert();
  };

  const columnMap = useSessionDocumentColumnMap({
    onDeleteAction: onConfirmDelete,
    onSubmittedAction: onConfirmSubmitted,
    onRevertAction: onConfirmRevert,
  });

  const columns = columnNames.reduce((acc, columnName) => {
    if (columnMap[columnName]) {
      acc.push(columnMap[columnName]);
    }
    return acc;
  }, []);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" sx={{mb: 1}}>
        <Box>
          {title && title.length > 1 && (
            <Typography variant="h5" color="darkest.main">
              {title}
            </Typography>
          )}
          {description && description.length > 0 && (
            <Typography variant="body2" color="darkest.main">
              {description}
            </Typography>
          )}
        </Box>

        <ListFilters
          showMemberFilter={showMemberFilter}
          showStaffFilter={showStaffFilter}
          showProgramFilter={showProgramFilter}
          onSubmit={onFilterFormSubmit}
        />
      </Stack>

      {showDocumentCount && (
        <RowCount
          isLoading={query.isFetching}
          label="Document"
          count={query.data?.count ?? 0}
        />
      )}

      <Paper elevation={1}>
        <DataGrid
          autoHeight
          variant="striped"
          density="comfortable"
          columns={columns}
          loading={query.isFetching}
          // $FlowFixMe - DataGrid types are wrong in Common Libs
          rows={rows}
          rowCount={query.data?.count ?? 0}
          noRowsText="No session documents"
          paginationMode="server"
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          rowsPerPageOptions={[25, 50, 100]}
          initialState={{
            sorting: {
              sortModel: getSortModel(ordering),
            },
          }}
          page={page - 1}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={onPageSizeChange}
          onSortModelChange={onSortModelChange}
          onRowClick={({row}) =>
            navigate(getDocumentUrl(row.member.id, row.id))
          }
          sx={{
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
          }}
          // When grid header is hidden we switch row background colors
          {...(!showGridHeader && {
            headerHeight: 0,
            getRowClassName: (rowParams) =>
              rowParams.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even',
          })}
          {...(rowHeight && {getRowHeight: rowHeight})}
        />
      </Paper>

      <ConfirmSubmit
        documentation={documentToSubmit}
        open={Boolean(documentToSubmit)}
        setOpen={closeSubmittedModal}
        onConfirm={onSubmitted}
      />

      <ConfirmDelete
        open={Boolean(documentToDelete)}
        setOpen={closeDeleteModal}
        onConfirm={onDelete}
      />

      <ConfirmRevert
        open={Boolean(documentToRevert)}
        setOpen={closeRevertModal}
        onConfirm={onRevert}
      />
    </>
  );
};
